import { useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, DragOverlay, pointerWithin } from '@dnd-kit/core';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { TFunction } from 'i18next';

import { readQuantity } from 'support/helpers/packingList/packableQuantity';
import { Button } from 'components/Form/Button/Button';

import { useApplyPackingList } from './hooks/useApplyPackingList';
import { useCheckboxSelection } from './hooks/useCheckboxSelection';
import { useDerivedPackingList } from './hooks/useDerivedPackingList';
import { useDiscardGuard } from './hooks/useDiscardGuard';
import { usePackingDialogs } from './hooks/usePackingDialogs';
import { usePackingDragAndDrop } from './hooks/usePackingDragAndDrop';
import { DiscardChangesDialog } from './components/DiscardChangesDialog/DiscardChangesDialog';
import { DropQuantityDialog } from './components/DropQuantityDialog/DropQuantityDialog';
import { MoveToDialog } from './components/MoveToDialog/MoveToDialog';
import { PackingListSection } from './components/PackingListSection/PackingListSection';
import { PlacementQuantityDialog } from './components/PlacementQuantityDialog/PlacementQuantityDialog';
import { ProductCardContent } from './components/ProductCardContent/ProductCardContent';
import { ProductsList } from './components/ProductsList/ProductsList';
import { UnitNestingWarningDialog } from './components/UnitNestingWarningDialog/UnitNestingWarningDialog';
import { UnplacedItemsWarningDialog } from './components/UnplacedItemsWarningDialog/UnplacedItemsWarningDialog';
import { ADD_ITEM_DROPPABLE_PREFIX, NEW_TU_DROPPABLE_ID } from './constants';
import { getDialogLineItem } from './helpers/lineItemDisplay';
import { placedKey } from './helpers/placedKey';
import {
  getLeafUnitOptions,
  getUnitTypeOptions,
  initialize,
  packingListReducer,
  UnitTypeOption,
} from './state/packingListReducer';
import { PackingListModalProps, PackingListTree } from './types';

const DEFAULT_DROP_ANIMATION = undefined;
const NO_DROP_ANIMATION = null;

export const PackingListModal = ({
  lineItems,
  transportUnits,
  transportUnitsConfig,
  quantityFieldPath = 'shipped_quantity',
  existingUnitIdentifiers = [],
  generateUnitIdentifiers,
  onApply,
  onClose,
}: PackingListModalProps) => {
  const { t } = useTranslation();
  const [packingList, dispatch] = useReducer(
    packingListReducer,
    { lineItems, transportUnits, quantityFieldPath },
    initialize,
  );
  const productSelection = useCheckboxSelection();
  const placedSelection = useCheckboxSelection();
  const { dispatchAndMark, requestClose, showDiscardConfirm, confirmDiscard, cancelDiscard } = useDiscardGuard(
    dispatch,
    onClose,
  );
  const unitTypeOptions = useMemo(() => getUnitTypeOptions(transportUnitsConfig), [transportUnitsConfig]);
  const { products, remainingByLineItem, placedContentByKey, tuPositionById, placed, total, unplacedCount } =
    useDerivedPackingList(packingList, quantityFieldPath);
  const dialogs = usePackingDialogs({ placedContentByKey, remainingByLineItem });
  const activeDialog = dialogs.activeDialog;

  const dnd = usePackingDragAndDrop({
    packingList,
    quantityFieldPath,
    productSelection,
    placedSelection,
    remainingByLineItem,
    placedContentByKey,
    tuPositionById,
    dispatchAndMark,
    unitTypeOptions,
    openBulkDrop: dialogs.openBulkDrop,
    t,
  });

  const apply = useApplyPackingList({
    packingList,
    quantityFieldPath,
    existingUnitIdentifiers,
    generateUnitIdentifiers,
    onApply,
    unplacedCount,
    t,
  });

  return (
    <>
      <Dialog open onClose={requestClose} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-gray-500/75" />
        <DialogPanel className="fixed inset-0 m-4 flex flex-col rounded-lg bg-white">
          <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('webedi:packing_list.title')}</h2>
            <button
              type="button"
              onClick={requestClose}
              aria-label={t('webedi:packing_list.close')}
              className="rounded-md p-1 text-gray-400 hover:text-gray-600 focus-visible:ring-2 focus-visible:ring-procuros-green-400 focus-visible:outline-hidden"
            >
              <XMarkIcon className="size-5" aria-hidden="true" />
            </button>
          </div>
          <DndContext
            sensors={dnd.sensors}
            collisionDetection={pointerWithin}
            onDragStart={dnd.handleDragStart}
            onDragEnd={dnd.handleDragEnd}
            onDragCancel={dnd.handleDragCancel}
          >
            <div className="flex flex-1 overflow-hidden">
              <ProductsList
                products={products}
                remainingByLineItem={remainingByLineItem}
                quantityFieldPath={quantityFieldPath}
                placedCount={placed}
                totalCount={total}
                selectedIds={productSelection.selectedIds}
                onToggleSelect={productSelection.toggle}
                onClearSelection={productSelection.clear}
                onMoveTo={(lineItemId) => dialogs.openMoveTo(null, lineItemId)}
              />
              <PackingListSection
                transportUnits={packingList.transportUnits}
                lineItemsById={packingList.lineItems}
                unitTypeOptions={unitTypeOptions}
                onChangeUnitType={(tuId, unitType) => dispatchAndMark({ type: 'SET_UNIT_TYPE', tuId, unitType })}
                onDeleteTransportUnit={(tuId) => dispatchAndMark({ type: 'DELETE_TU', tuId })}
                onRemoveItem={(lineItemId, tuId) => dispatchAndMark({ type: 'REMOVE_ITEM', lineItemId, tuId })}
                selectedPlacedKeys={placedSelection.selectedIds}
                onTogglePlaced={(tuId, lineItemId) => placedSelection.toggle(placedKey(tuId, lineItemId))}
                onMoveToItem={(tuId, lineItemId) => dialogs.openMoveTo(tuId, lineItemId)}
                onEditItemQuantity={(tuId, lineItemId) => dialogs.openEditQuantity(tuId, lineItemId)}
              />
            </div>
            <DragOverlay
              className="cursor-grabbing"
              dropAnimation={dnd.skipDropAnimation ? NO_DROP_ANIMATION : DEFAULT_DROP_ANIMATION}
            >
              {dnd.beingDraggedEntry ? (
                dnd.draggedCount > 1 ? (
                  <div className="rounded-md bg-procuros-green-600 px-3 py-2 text-sm font-medium text-white shadow-lg">
                    {t('webedi:packing_list.products.selected_count', { count: dnd.draggedCount })}
                  </div>
                ) : (
                  <ProductCardContent
                    entry={dnd.beingDraggedEntry}
                    available={remainingByLineItem.get(dnd.beingDraggedEntry.id) ?? 0}
                    total={readQuantity(dnd.beingDraggedEntry.row[quantityFieldPath])}
                    isOverlay
                  />
                )
              ) : null}
            </DragOverlay>
          </DndContext>
          <div className="flex shrink-0 items-center justify-between border-t border-gray-200 px-6 py-4">
            <span className={`text-sm ${unplacedCount > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
              {t('webedi:packing_list.unplaced_count', { count: unplacedCount })}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={requestClose} analyticsId="webedi:packing_list_cancel">
                {t('common:cancel')}
              </Button>
              <Button onClick={apply.handleApply} loading={apply.isApplying} analyticsId="webedi:packing_list_apply">
                {t('webedi:packing_list.apply')}
              </Button>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
      <ApplyWarningDialog apply={apply} unitTypeOptions={unitTypeOptions} />
      <MoveToPackingDialog packingList={packingList} dialogs={dialogs} dnd={dnd} t={t} />
      {activeDialog?.kind === 'editQuantity' ? (
        <PlacementQuantityDialog
          {...getDialogLineItem(packingList.lineItems[activeDialog.lineItemId], t)}
          remaining={
            (placedContentByKey.get(placedKey(activeDialog.tuId, activeDialog.lineItemId))?.quantity ?? 0) +
            (remainingByLineItem.get(activeDialog.lineItemId) ?? 0)
          }
          initialQuantity={placedContentByKey.get(placedKey(activeDialog.tuId, activeDialog.lineItemId))?.quantity ?? 0}
          onConfirm={(quantity) => {
            const activeDialog = dialogs.activeDialog;
            if (activeDialog?.kind !== 'editQuantity') {
              return;
            }
            dispatchAndMark({
              type: 'SET_ITEM_QUANTITY',
              tuId: activeDialog.tuId,
              lineItemId: activeDialog.lineItemId,
              quantity,
            });
            dialogs.close();
          }}
          onCancel={dialogs.close}
        />
      ) : null}
      {activeDialog?.kind === 'bulkDrop' ? (
        <DropQuantityDialog
          title={activeDialog.request.title}
          rows={activeDialog.request.entries}
          confirmLabel={activeDialog.request.confirmLabel}
          onConfirm={(quantities) => {
            const activeDialog = dialogs.activeDialog;
            if (activeDialog?.kind !== 'bulkDrop') {
              return;
            }
            const { request } = activeDialog;
            const entriesWithQuantity = request.entries.map((entry) => ({
              ...entry,
              quantity: quantities[entry.key] ?? 0,
            }));
            dnd.applyDrop(entriesWithQuantity, request.over);
            if (request.usingSelection) {
              productSelection.clear();
              placedSelection.clear();
            }
            dialogs.close();
          }}
          onCancel={dialogs.close}
        />
      ) : null}
      {showDiscardConfirm ? <DiscardChangesDialog onConfirm={confirmDiscard} onCancel={cancelDiscard} /> : null}
    </>
  );
};

/** The gates Apply runs before serializing, rendered one at a time in the order they are asked. */
const ApplyWarningDialog = ({
  apply,
  unitTypeOptions,
}: {
  apply: ReturnType<typeof useApplyPackingList>;
  unitTypeOptions: Array<UnitTypeOption>;
}) => {
  if (apply.activeWarning === 'unplaced') {
    return <UnplacedItemsWarningDialog onConfirm={apply.confirmWarning} onCancel={apply.dismissWarning} />;
  }
  if (apply.activeWarning === 'nesting') {
    return (
      <UnitNestingWarningDialog
        nestings={apply.invalidNestings}
        unitTypeOptions={unitTypeOptions}
        onConfirm={apply.confirmWarning}
        onCancel={apply.dismissWarning}
      />
    );
  }
  return null;
};

type MoveToPackingDialogProps = {
  packingList: PackingListTree;
  dialogs: ReturnType<typeof usePackingDialogs>;
  dnd: ReturnType<typeof usePackingDragAndDrop>;
  t: TFunction;
};

const MoveToPackingDialog = ({ packingList, dialogs, dnd, t }: MoveToPackingDialogProps) => {
  const activeDialog = dialogs.activeDialog;
  if (activeDialog?.kind !== 'moveTo') {
    return null;
  }

  return (
    <MoveToDialog
      {...getDialogLineItem(packingList.lineItems[activeDialog.lineItemId], t)}
      destinations={getLeafUnitOptions(packingList.transportUnits, activeDialog.tuId ?? undefined)}
      max={dialogs.moveToMax}
      onConfirm={(destination, quantity) => {
        const currentDialog = dialogs.activeDialog;
        if (currentDialog?.kind !== 'moveTo') {
          return;
        }
        const entry = {
          ...getDialogLineItem(packingList.lineItems[currentDialog.lineItemId], t),
          key: currentDialog.lineItemId,
          lineItemId: currentDialog.lineItemId,
          fromTuId: currentDialog.tuId ?? undefined,
          max: dialogs.moveToMax,
          quantity,
        };
        const over =
          destination.kind === 'new' ? NEW_TU_DROPPABLE_ID : `${ADD_ITEM_DROPPABLE_PREFIX}${destination.tuId}`;
        dnd.applyDrop([entry], over);
        dialogs.close();
      }}
      onCancel={dialogs.close}
    />
  );
};
